import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { Result } from "better-result";
import {
  getLesadministratieLeerlingTimeouts,
  getLesadministratieStatistieken,
  getLesadministratieStatistiekLeerlingen,
} from "#/modules/lesadministratie.statistics/api.lesadministratie-statistieken.server";
import {
  effectiveKlasIdsForLesadministratieStatistieken,
  getDefaultDateRange,
} from "#/modules/lesadministratie.statistics/filters.lesadministratie-statistieken";
import type { LesadministratieStatistiekenFilters } from "#/modules/lesadministratie.statistics/schema.search";
import { isLesadministratieStatKpi } from "#/modules/lesadministratie.statistics/lesadministratie-stat-kpi";
import { getClassesForStartLesson } from "#/modules/school.klassen/api.klassen.server";
import { LESSON_ADMINISTRATION_SEARCH_DEFAULTS } from "#/modules/lesadministratie.statistics/schema.search";

export const getLessonAdministrationData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { filters: LesadministratieStatistiekenFilters }) => data)
  .handler(async ({ data }) => {
    const { filters } = data;
    const defaultRange = getDefaultDateRange();
    const fromCalendar = filters.from ?? defaultRange.from;
    const toCalendar = filters.to ?? defaultRange.to;

    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    if (klassen.length === 0) {
      return { klassen, stats: null };
    }

    const allIds = klassen.map((k) => k.id);
    const effectiveKlasIds = effectiveKlasIdsForLesadministratieStatistieken(filters, allIds);

    const result = await getLesadministratieStatistieken({
      klasIds: effectiveKlasIds,
      fromCalendar,
      toCalendar,
    });

    if (Result.isError(result)) {
      throw result.error;
    }

    return { klassen, stats: result.value };
  });

export const getLessonAdministrationKpiData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    (data: {
      filters: LesadministratieStatistiekenFilters;
      kpi: string;
      locale: string;
      school: string;
      schoolyear?: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const listRedirect = {
      to: "/$locale/{-$schoolyear}/$school/insights/lesson-administration",
      params: { locale: data.locale, schoolyear: data.schoolyear, school: data.school },
      search: { klasIds: LESSON_ADMINISTRATION_SEARCH_DEFAULTS.klasIds },
    } as const;

    if (!isLesadministratieStatKpi(data.kpi)) {
      throw redirect(listRedirect);
    }

    const kpi = data.kpi;
    const { filters } = data;
    const defaultRange = getDefaultDateRange();
    const fromCalendar = filters.from ?? defaultRange.from;
    const toCalendar = filters.to ?? defaultRange.to;

    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    if (klassen.length === 0) {
      throw redirect(listRedirect);
    }

    const allIds = klassen.map((k) => k.id);
    const effectiveKlasIds = effectiveKlasIdsForLesadministratieStatistieken(filters, allIds);

    const result = await getLesadministratieStatistiekLeerlingen(kpi, {
      klasIds: effectiveKlasIds,
      fromCalendar,
      toCalendar,
    });

    if (Result.isError(result)) {
      throw result.error;
    }

    return { kpi, data: result.value };
  });

export const getLessonAdministrationLeerlingTimeouts = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    (data: {
      filters: LesadministratieStatistiekenFilters;
      leerlingId: string;
      locale: string;
      school: string;
      schoolyear?: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const listRedirect = {
      to: "/$locale/{-$schoolyear}/$school/insights/lesson-administration",
      params: { locale: data.locale, schoolyear: data.schoolyear, school: data.school },
      search: { klasIds: LESSON_ADMINISTRATION_SEARCH_DEFAULTS.klasIds },
    } as const;

    const { filters } = data;
    const defaultRange = getDefaultDateRange();
    const fromCalendar = filters.from ?? defaultRange.from;
    const toCalendar = filters.to ?? defaultRange.to;

    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    if (klassen.length === 0) {
      throw redirect(listRedirect);
    }

    const allIds = klassen.map((k) => k.id);
    const effectiveKlasIds = effectiveKlasIdsForLesadministratieStatistieken(filters, allIds);

    const result = await getLesadministratieLeerlingTimeouts({
      leerlingId: data.leerlingId,
      klasIds: effectiveKlasIds,
      fromCalendar,
      toCalendar,
    });

    if (Result.isError(result)) {
      throw result.error;
    }

    return { data: result.value };
  });
